import { motion } from "framer-motion";
import { Medal, Handshake, Ruler, UsersThree } from "@phosphor-icons/react";
import { PageHero } from "../components/PageHero";
import { CTASection } from "../components/home/CTASection";
import { useSettings } from "../lib/api";

const TEAM_IMG =
  "https://static.prod-images.emergentagent.com/jobs/85cac4e7-9373-4ca5-a120-c8a76e0a0606/images/e89ecabe8057d6175c39db966e73e17746de92601297162300a52c78d2fd19f0.png";

const values = [
  { icon: Medal, title: "Discipline", text: "Military-grade planning, accountability, and follow-through on every scope, schedule, and dollar." },
  { icon: Handshake, title: "Integrity", text: "Transparent pricing, honest timelines, and communication you can build a partnership on." },
  { icon: Ruler, title: "Craft", text: "Work that meets spec, passes inspection the first time, and stands for decades." },
  { icon: UsersThree, title: "Community", text: "We hire local, train local, and reinvest in the Detroit neighborhoods we rebuild." },
];

export default function About() {
  const { data: s } = useSettings();

  return (
    <div data-testid="about-page">
      <PageHero
        overline="About FOSCHE, LLC"
        title="Forged in service. Built for Detroit."
        subtitle="A service-disabled veteran-owned firm applying mission discipline to construction, redevelopment, and infrastructure."
      />

      <section className="bg-canvas py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="overline-label mb-4">Our Story</p>
            <h2 className="font-heading font-medium text-3xl md:text-4xl tracking-tight text-ink">
              From the uniform to the hard hat, the mission never changed.
            </h2>
            <div className="mt-7 space-y-5 text-sub leading-relaxed">
              <p>
                FOSCHE, LLC was founded on a simple conviction: the same discipline that serves the nation can rebuild
                its cities. Headquartered in Detroit, we operate at the intersection of construction, restoration, and
                community redevelopment — delivering for federal agencies, commercial developers, investors, and
                homeowners alike.
              </p>
              <p>
                As a Service-Disabled Veteran-Owned Small Business, we understand compliance, chain of command, and
                mission execution. Every project gets a clear plan, a accountable leader, and a standard of work that
                does not bend.
              </p>
              <p>
                We measure success in more than square footage — in blocks stabilized, families housed, and
                neighborhoods that believe in their future again.
              </p>
            </div>

            <div className="mt-10 border border-line p-7 bg-panel max-w-md" data-testid="founder-card">
              <p className="overline-label mb-3">Leadership</p>
              <p className="font-heading font-medium text-2xl tracking-tight text-ink">{s?.founder_name || "Terry A. Foster"}</p>
              <p className="font-mono-tech text-xs tracking-wider text-sub mt-1.5">{s?.founder_title || "Founder & Principal"} · U.S. Military Veteran</p>
              <p className="mt-4 text-sm text-sub leading-relaxed">
                "We don't just take on projects. We take on responsibility — for the outcome, the community, and the
                people who trust us with both."
              </p>
            </div>
          </div>

          <div className="relative">
            <img src={TEAM_IMG} alt="Veteran and contractor walking through a FOSCHE construction site" className="w-full h-[420px] lg:h-[540px] object-cover border border-line" />
            <div className="absolute -bottom-6 -left-6 hidden md:grid grid-cols-2 gap-px bg-line border border-line">
              {[
                ["SDVOSB", "Certified"],
                ["Detroit", "Headquartered"],
              ].map(([a, b]) => (
                <div key={a} className="bg-canvas px-6 py-5">
                  <p className="font-heading font-medium text-xl tracking-tight text-ink">{a}</p>
                  <p className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-sub mt-1">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-panel border-y border-line py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="overline-label mb-4">Operating Principles</p>
          <h2 className="font-heading font-medium text-3xl md:text-4xl tracking-tight text-ink mb-14">
            How we work.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`p-8 border-line border-t sm:border-t-0 ${i > 0 ? "sm:border-l" : ""}`}
              >
                <v.icon size={28} className="text-blueprint mb-5" />
                <h3 className="font-heading font-medium text-xl tracking-tight text-ink">{v.title}</h3>
                <p className="mt-3 text-sm text-sub leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
